import React, { createContext, useContext, useState, useCallback } from 'react';
import { login as apiLogin, register as apiRegister } from '../services/api';

const AuthContext = createContext(null);

function readUser() {
  const saved = localStorage.getItem('user') || sessionStorage.getItem('user');
  return saved ? JSON.parse(saved) : null;
}

function persistAuth(data, remember) {
  const store = remember ? localStorage : sessionStorage;
  const other = remember ? sessionStorage : localStorage;
  other.removeItem('token');
  other.removeItem('user');
  store.setItem('token', data.token);
  store.setItem('user', JSON.stringify(data));
}

function clearAuth() {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  sessionStorage.removeItem('token');
  sessionStorage.removeItem('user');
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(readUser);

  const loginUser = useCallback(async (credentials) => {
    const { rememberMe = true, ...payload } = credentials;
    const { data } = await apiLogin(payload);
    persistAuth(data, rememberMe);
    setUser(data);
    return data;
  }, []);

  const registerUser = useCallback(async (info) => {
    const { data } = await apiRegister(info);
    persistAuth(data, true);
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(() => {
    clearAuth();
    setUser(null);
  }, []);

  const updateUser = useCallback((patch) => {
    setUser(prev => {
      const next = { ...(prev || {}), ...patch };
      const store = localStorage.getItem('user') ? localStorage : sessionStorage;
      store.setItem('user', JSON.stringify(next));
      return next;
    });
  }, []);

  return (
    <AuthContext.Provider value={{ user, loginUser, registerUser, logout, updateUser, isAuthenticated: !!user }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
